import './index.css';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef, useId, useState, useCallback, useEffect, Fragment, cloneElement } from 'react';
import classnames from 'classnames';
import { getOffsetElement } from '@hh.ru/magritte-common-metrics';
import { useWhenFontLoaded } from '@hh.ru/magritte-common-use-when-font-loaded';
import { DEFAULT_SIZE } from './types.js';
import { s as styles } from './badge-Cka7SRap.js';

const BADGE_OUTLINE_PX = 2;
const FOCUS_OUTLINE_PX = 4;
const ComponentWithBadge = ({ allowedSizes = null, badge, children, offset = 0, stretched = false, outline = FOCUS_OUTLINE_PX, }) => {
    const wrapperRef = useRef(null);
    const componentRef = useRef(null);
    const badgeRef = useRef(null);
    const clipPathRef = useRef(null);
    const clipPathId = useId();
    const [isDisplayed, setDisplayed] = useState(false);
    const [isFontLoaded, setFontLoaded] = useState(false);
    const badgeProps = badge?.props;
    const badgeText = badgeProps?.children;
    const badgeSize = badgeProps?.size ?? DEFAULT_SIZE;
    const recalcClipPath = useCallback(() => {
        if (!wrapperRef.current || !componentRef.current || !badgeRef.current || !clipPathRef.current) {
            return;
        }
        wrapperRef.current.style.setProperty(`--magritte-badge-offset`, `${offset}px`);
        const { offsetLeft: badgeX, offsetTop: badgeY } = getOffsetElement(badgeRef.current, wrapperRef.current);
        const { width: badgeWidth, height: badgeHeight } = badgeRef.current.getBoundingClientRect();
        const borderRadius = badgeHeight / 2;
        const outlineRadius = borderRadius + BADGE_OUTLINE_PX;
        // координаты (0, 0) в svg соответствует левому верхнему углу контента, (1, 1) — правому нижнему углу
        // поэтому нужно посчитать размер одного пикселя в системе координат svg
        const { width: wrapperWidth, height: wrapperHeight } = wrapperRef.current.getBoundingClientRect();
        const xPixelSize = 1 / wrapperWidth;
        const yPixelSize = 1 / wrapperHeight;
        const xSize = (size) => size * xPixelSize;
        const ySize = (size) => size * yPixelSize;
        // прямоугольник по границам компонента с учетом аутлайна фокуса (или кастомного аутлайна)
        const componentOutline = [
            { x: -xSize(outline), y: -ySize(outline) },
            { x: 1 + xSize(outline), y: -ySize(outline) },
            { x: 1 + xSize(outline), y: 1 + ySize(outline) },
            { x: -xSize(outline), y: 1 + ySize(outline) },
        ];
        // прямоугольник с двумя полуокружностями по бокам по границам бейджа с учетом прозрачного аутлайна
        const badgeOutline = [
            { x: xSize(badgeX + borderRadius), y: ySize(badgeY - BADGE_OUTLINE_PX) },
            { x: xSize(badgeX + borderRadius), y: ySize(badgeY + badgeHeight + BADGE_OUTLINE_PX) },
            { x: xSize(badgeX + badgeWidth - borderRadius), y: ySize(badgeY + badgeHeight + BADGE_OUTLINE_PX) },
            { x: xSize(badgeX + badgeWidth - borderRadius), y: ySize(badgeY - BADGE_OUTLINE_PX) },
        ];
        const clipPath = [
            // // M (move) — перейти на точку, ничего не рисовать
            `M ${componentOutline[0].x}, ${componentOutline[0].y}`,
            // L (line) — нарисовать прямую линию
            `L ${componentOutline[1].x}, ${componentOutline[1].y}`,
            `L ${componentOutline[2].x}, ${componentOutline[2].y}`,
            `L ${componentOutline[3].x}, ${componentOutline[3].y}`,
            `L ${componentOutline[0].x}, ${componentOutline[0].y}`,
            `M ${badgeOutline[0].x}, ${badgeOutline[0].y}`,
            // A (arc) — нарисовать часть эллипса
            `A ${xSize(outlineRadius)}, ${ySize(outlineRadius)}, 0, 0, 0, ${badgeOutline[1].x}, ${badgeOutline[1].y}`,
            `L ${badgeOutline[2].x}, ${badgeOutline[2].y}`,
            `A ${xSize(outlineRadius)}, ${ySize(outlineRadius)}, 0, 0, 0, ${badgeOutline[3].x}, ${badgeOutline[3].y}`,
            `L ${badgeOutline[0].x}, ${badgeOutline[0].y}`,
        ].join(` `);
        clipPathRef.current.querySelector(`path`)?.setAttribute(`d`, clipPath);
        clipPathRef.current.setAttribute(`id`, `clip-path-${clipPathId}`);
        componentRef.current.style.setProperty(`clip-path`, `url(#clip-path-${clipPathId})`);
    }, [clipPathId, offset, outline]);
    // eslint-disable-next-line disable-autofix/react-hooks/exhaustive-deps
    useEffect(() => {
        if (wrapperRef.current !== null) {
            setDisplayed(wrapperRef.current.offsetParent !== null);
        }
    });
    useWhenFontLoaded(() => {
        setFontLoaded(true);
    });
    useEffect(() => {
        if (isDisplayed && isFontLoaded) {
            recalcClipPath();
        }
    }, [badgeText, badgeSize, isDisplayed, isFontLoaded, recalcClipPath, children]);
    if (!badge || (allowedSizes !== null && !allowedSizes.includes(badgeSize))) {
        return jsx(Fragment, { children: children });
    }
    return (jsxs("div", { className: classnames(styles.componentWithBadge, { [styles.componentWithBadgeStretched]: stretched }), ref: wrapperRef, children: [jsx("div", { ref: componentRef, children: children }), jsx("div", { className: classnames(styles.badgeWrapper, { [styles.invisibleWrapper]: !isFontLoaded }), children: cloneElement(badge, { ref: badgeRef }) }), jsx("svg", { className: styles.clipPath, children: jsx("defs", { children: jsx("clipPath", { clipPathUnits: "objectBoundingBox", ref: clipPathRef, children: jsx("path", {}) }) }) })] }));
};

export { ComponentWithBadge };
//# sourceMappingURL=ComponentWithBadge.js.map
